import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { projectService } from '../../services/project.service';
import { getLeads as getLeadsService } from '../../services/lead.service';
import { getUserById } from '../../services/user.service';
import { workloadService } from '../../services/workload.service';
import { addressService } from '../../services/address.service';
import { surveyResponseService } from '../../services/surveyResponse.service';
import { surveyService } from '../../services/survey.service';
// New comprehensive reporting services
import {
  projectReportsService,
  canvasserReportsService,
  surveyReportsService,
  projectLifecycleService,
} from '../../services/reporting';
import type {
  ProjectOverviewReport as ProjectOverviewReportType,
  CanvasserPerformanceSummary,
  SurveyAnalyticsReport as SurveyAnalyticsReportType,
  QuestionAnalytics,
  ProjectLifecycleReport as ProjectLifecycleReportType,
} from '../../services/reporting';
import { Project } from '../../types/project.types';
import { Lead, LeadStatus, LeadDetermination } from '../../types/lead.types';
import { Workload } from '../../types/workload.types';
import { format, subDays, subWeeks, subMonths } from 'date-fns';
import { Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { buildLifecyclePdf } from '../../utils/lifecyclePdf';
// Report components
import {
  PerformanceReport,
  SourceAnalysisReport,
  DemographicsReport,
  StatusBreakdownReport,
  DeterminationBreakdownReport,
  TimeRangeReport,
  WorkloadsReport,
  SurveysReport,
  ProjectOverviewReport,
  CanvasserPerformanceReport,
  SurveyAnalyticsReport,
  ProjectLifecycleReport,
} from '../../components/reports';

interface TimeRangeOption {
  value: string;
  label: string;
  getDates: () => { start: Date; end: Date };
}

const timeRangeOptions: TimeRangeOption[] = [
  { value: '1day', label: '1 Day', getDates: () => ({ start: subDays(new Date(), 1), end: new Date() }) },
  { value: '3days', label: '3 Days', getDates: () => ({ start: subDays(new Date(), 3), end: new Date() }) },
  { value: '5days', label: '5 Days', getDates: () => ({ start: subDays(new Date(), 5), end: new Date() }) },
  { value: '7days', label: '7 Days', getDates: () => ({ start: subDays(new Date(), 7), end: new Date() }) },
  { value: '2weeks', label: '2 Weeks', getDates: () => ({ start: subWeeks(new Date(), 2), end: new Date() }) },
  { value: '3weeks', label: '3 Weeks', getDates: () => ({ start: subWeeks(new Date(), 3), end: new Date() }) },
  { value: '4weeks', label: '4 Weeks', getDates: () => ({ start: subWeeks(new Date(), 4), end: new Date() }) },
  { value: '5weeks', label: '5 Weeks', getDates: () => ({ start: subWeeks(new Date(), 5), end: new Date() }) },
  { value: '6weeks', label: '6 Weeks', getDates: () => ({ start: subWeeks(new Date(), 6), end: new Date() }) },
  { value: '3months', label: '3 Months', getDates: () => ({ start: subMonths(new Date(), 3), end: new Date() }) },
  { value: '6months', label: '6 Months', getDates: () => ({ start: subMonths(new Date(), 6), end: new Date() }) },
  { value: 'alltime', label: 'All Time', getDates: () => ({ start: new Date(2020, 0, 1), end: new Date() }) }
];

type ReportType = 'lifecycle' | 'performance' | 'source' | 'demographics' | 'timerange' | 'statusbreakdown' | 'determinationbreakdown' | 'workloads' | 'surveys' | 'project_overview' | 'canvasser_performance' | 'survey_analytics';

// Helper function to get leads for a project within a time range
const getLeads = async (projectId: string, startDate: Date, endDate: Date): Promise<Lead[]> => {
  try {
    const allLeads = await getLeadsService({ projectId });
    return allLeads.filter(lead => {
      const createdAt = lead.createdAt?.toDate?.() || new Date(lead.createdAt);
      return createdAt >= startDate && createdAt <= endDate;
    });
  } catch (error) {
    console.error('Error fetching leads:', error);
    return [];
  }
};

interface ReportsData {
  fieldStaffPerformance: Array<{
    name: string;
    leads: number;
    conversions: number;
    conversionRate: number;
  }>;
  sourceAnalysis: Array<{
    source: string;
    leads: number;
    conversions: number;
    conversionRate: number;
  }>;
  demographicsData: {
    gradeDistribution: Array<{ grade: string; count: number }>;
    contactPreferences: Array<{ method: string; count: number }>;
    zipCodeDistribution: Array<{ zipCode: string; count: number }>;
  };
  timeRangeData: Array<{
    period: string;
    leads: number;
    validLeads: number;
    enrollments: number;
    students: number;
    conversionRate: number;
  }>;
  statusBreakdown: Array<{
    status: string;
    count: number;
    percentage: number;
    color: string;
  }>;
  determinationBreakdown: Array<{
    determination: string;
    count: number;
    percentage: number;
    color: string;
  }>;
  dailyGeneration: Array<{
    date: string;
    parents: number;
    students: number;
  }>;
  workloadData: {
    workloads: Array<{
      id: string;
      name: string;
      status: string;
      totalAddresses: number;
      completedAddresses: number;
      pendingAddresses: number;
      notHomeAddresses: number;
      completionRate: number;
      region?: string;
      assignedTo: string[];
    }>;
    regionSummary: Array<{
      region: string;
      workloadCount: number;
      totalHomes: number;
      completedHomes: number;
      completionRate: number;
    }>;
    statusDistribution: Array<{
      status: string;
      count: number;
      color: string;
    }>;
  };
  surveyData: {
    totalResponses: number;
    responsesByWorkload: Array<{
      workloadName: string;
      responses: number;
      completionRate: number;
    }>;
    questionBreakdown: Array<{
      question: string;
      answers: Array<{
        answer: string;
        count: number;
        percentage: number;
      }>;
    }>;
  };
}

const ReportsPage = () => {
  const { currentUser, userProfile } = useAuth();
  const { projects, selectedProjectId, selectedProject } = useProject();
  const [reportType, setReportType] = useState<ReportType>('performance');
  const [timeRange, setTimeRange] = useState<string>('6months');
  const [loading, setLoading] = useState(false);
  const [reportsData, setReportsData] = useState<ReportsData>({
    fieldStaffPerformance: [],
    sourceAnalysis: [],
    demographicsData: {
      gradeDistribution: [],
      contactPreferences: [],
      zipCodeDistribution: []
    },
    timeRangeData: [],
    statusBreakdown: [],
    determinationBreakdown: [],
    dailyGeneration: [],
    workloadData: {
      workloads: [],
      regionSummary: [],
      statusDistribution: [],
    },
    surveyData: {
      totalResponses: 0,
      responsesByWorkload: [],
      questionBreakdown: [],
    },
  });
  const reportRef = useRef<HTMLDivElement>(null);

  // Comprehensive report data
  const [projectOverview, setProjectOverview] = useState<ProjectOverviewReportType | null>(null);
  const [canvasserSummaries, setCanvasserSummaries] = useState<CanvasserPerformanceSummary[]>([]);
  const [surveyAnalytics, setSurveyAnalytics] = useState<SurveyAnalyticsReportType | null>(null);
  const [selectedSurveyId, setSelectedSurveyId] = useState<string>('');
  const [availableSurveys, setAvailableSurveys] = useState<{id: string; name: string}[]>([]);
  const [lifecycleReport, setLifecycleReport] = useState<ProjectLifecycleReportType | null>(null);

  // Load report data when project changes
  useEffect(() => {
    if (selectedProjectId) {
      loadReportData();
    }
  }, [selectedProjectId, reportType, timeRange]);

  const loadReportData = async () => {
    if (!selectedProjectId) return;
    
    setLoading(true);
    try {
      const timeRangeOption = timeRangeOptions.find(t => t.value === timeRange);
      const { start, end } = timeRangeOption!.getDates();
      
      // Load all leads for the project within time range
      const leads = await getLeads(selectedProjectId, start, end);
      
      // Process data based on report type
      switch (reportType) {
        case 'lifecycle':
          await loadLifecycleReport(start, end);
          break;
        case 'performance':
          await loadPerformanceData(leads);
          break;
        case 'source':
          await loadSourceAnalysis(leads);
          break;
        case 'demographics':
          await loadDemographicsData(leads);
          break;
        case 'timerange':
          await loadTimeRangeData(leads);
          break;
        case 'statusbreakdown':
          await loadStatusBreakdown(leads);
          break;
        case 'determinationbreakdown':
          await loadDeterminationBreakdown(leads);
          break;
        case 'workloads':
          await loadWorkloadData();
          break;
        case 'surveys':
          await loadSurveyData();
          break;
        // New comprehensive reports
        case 'project_overview':
          await loadProjectOverview(start, end);
          break;
        case 'canvasser_performance':
          await loadCanvasserPerformance(start, end);
          break;
        case 'survey_analytics':
          await loadSurveyAnalytics(start, end);
          break;
      }

      // Always load daily generation data for lead-related reports
      if (!['lifecycle', 'workloads', 'surveys', 'project_overview', 'canvasser_performance', 'survey_analytics'].includes(reportType)) {
        await loadDailyGeneration(leads);
      }
      
    } catch (error) {
      console.error('Error loading report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const loadPerformanceData = async (leads: Lead[]) => {
    // Group by field staff
    const staffMap = new Map<string, { name: string; leads: number; conversions: number }>();

    // Names that mean "we never resolved a real one" — fall back to user lookup.
    const isPlaceholderName = (n?: string) =>
      !n || !n.trim() || n === 'Field User' || n === 'Unknown' || n === 'User';

    for (const lead of leads) {
      const staffId = lead.capturedBy || 'unknown';
      const staffName = lead.capturedByName || 'Unknown';

      if (!staffMap.has(staffId)) {
        staffMap.set(staffId, { name: staffName, leads: 0, conversions: 0 });
      }

      const staff = staffMap.get(staffId)!;
      staff.leads++;
      if (lead.status === 'enrolled') {
        staff.conversions++;
      }
    }

    // For any staff entry that landed on a placeholder name, look up the
    // actual user record. Fixes leads created before the firstName/lastName
    // bug was patched without needing a data backfill.
    await Promise.all(
      Array.from(staffMap.entries()).map(async ([staffId, staff]) => {
        if (staffId === 'unknown' || !isPlaceholderName(staff.name)) return;
        try {
          const user = await getUserById(staffId);
          if (user?.name) staff.name = user.name;
          else if (user?.email) staff.name = user.email;
        } catch {
          // Leave the placeholder if the lookup fails.
        }
      })
    );

    const performance = Array.from(staffMap.values()).map(staff => ({
      name: staff.name,
      leads: staff.leads,
      conversions: staff.conversions,
      conversionRate: staff.leads > 0 ? (staff.conversions / staff.leads) * 100 : 0
    }));
    
    setReportsData(prev => ({ ...prev, fieldStaffPerformance: performance }));
  };

  const loadSourceAnalysis = async (leads: Lead[]) => {
    const sourceMap = new Map<string, { leads: number; conversions: number }>();
    
    leads.forEach(lead => {
      const source = lead.source || 'Unknown';
      const existing = sourceMap.get(source) || { leads: 0, conversions: 0 };
      existing.leads++;
      if (lead.status === 'enrolled') {
        existing.conversions++;
      }
      sourceMap.set(source, existing);
    });
    
    const analysis = Array.from(sourceMap.entries()).map(([source, data]) => ({
      source,
      leads: data.leads,
      conversions: data.conversions,
      conversionRate: data.leads > 0 ? (data.conversions / data.leads) * 100 : 0
    }));
    
    setReportsData(prev => ({ ...prev, sourceAnalysis: analysis }));
  };

  const loadDemographicsData = async (leads: Lead[]) => {
    // Grade distribution
    const gradeMap = new Map<string, number>();
    leads.forEach(lead => {
      lead.children?.forEach(child => {
        const grade = child.grade || 'Unknown';
        gradeMap.set(grade, (gradeMap.get(grade) || 0) + 1);
      });
    });
    
    // Contact preferences
    const contactMap = new Map<string, number>();
    leads.forEach(lead => {
      const method = lead.preferredContactMethod || 'Unknown';
      contactMap.set(method, (contactMap.get(method) || 0) + 1);
    });
    
    // ZIP code distribution
    const zipMap = new Map<string, number>();
    leads.forEach(lead => {
      const zip = lead.zipCode || 'Unknown';
      zipMap.set(zip, (zipMap.get(zip) || 0) + 1);
    });
    
    setReportsData(prev => ({
      ...prev,
      demographicsData: {
        gradeDistribution: Array.from(gradeMap.entries()).map(([grade, count]) => ({ grade, count })),
        contactPreferences: Array.from(contactMap.entries()).map(([method, count]) => ({ method, count })),
        zipCodeDistribution: Array.from(zipMap.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)
          .map(([zipCode, count]) => ({ zipCode, count }))
      }
    }));
  };

  const loadTimeRangeData = async (leads: Lead[]) => {
    // Group by day for time range data
    const periodMap = new Map<string, { leads: number; students: number }>();
    
    leads.forEach(lead => {
      const date = lead.createdAt.toDate();
      const periodKey = format(date, 'MMM d');
      
      if (!periodMap.has(periodKey)) {
        periodMap.set(periodKey, { leads: 0, students: 0 });
      }
      
      const period = periodMap.get(periodKey)!;
      period.leads++;
      period.students += lead.children.length;
    });
    
    const data = Array.from(periodMap.entries())
      .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
      .map(([period, { leads, students }]) => ({
        date: period,
        parents: leads,
        students
      }));
    
    setReportsData(prev => ({ ...prev, dailyGeneration: data }));
  };

  const loadStatusBreakdown = async (leads: Lead[]) => {
    const statusCounts: Record<LeadStatus, number> = {
      uncontacted: 0,
      cold: 0,
      warm: 0,
      hot: 0,
      enrolled: 0,
      lost: 0,
      dnc: 0
    };
    
    leads.forEach(lead => {
      statusCounts[lead.status]++;
    });
    
    const total = leads.length;
    const breakdown = Object.entries(statusCounts).map(([status, count]) => ({
      status,
      count,
      percentage: (count / total) * 100,
      color: getStatusColor(status as LeadStatus)
    })).filter(s => s.count > 0);
    
    setReportsData(prev => ({ ...prev, statusBreakdown: breakdown }));
  };

  const loadDeterminationBreakdown = async (leads: Lead[]) => {
    const determinationCounts: Record<LeadDetermination, number> = {
      pending: 0,
      valid: 0,
      invalid: 0
    };
    
    leads.forEach(lead => {
      const det = lead.determination || 'pending';
      determinationCounts[det]++;
    });
    
    const total = leads.length;
    const breakdown = Object.entries(determinationCounts).map(([determination, count]) => ({
      determination,
      count,
      percentage: (count / total) * 100,
      color: getDeterminationColor(determination as LeadDetermination)
    }));
    
    setReportsData(prev => ({ ...prev, determinationBreakdown: breakdown }));
  };

  const loadDailyGeneration = async (leads: Lead[]) => {
    const dailyMap = new Map<string, { parents: number; students: number }>();
    
    leads.forEach(lead => {
      const date = format(lead.createdAt.toDate(), 'MMM dd');
      
      if (!dailyMap.has(date)) {
        dailyMap.set(date, { parents: 0, students: 0 });
      }
      
      const daily = dailyMap.get(date)!;
      daily.parents++;
      daily.students += lead.children.length;
    });
    
    const data = Array.from(dailyMap.entries())
      .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
      .map(([date, { parents, students }]) => ({
        date,
        parents,
        students
      }));
    
    setReportsData(prev => ({ ...prev, dailyGeneration: data }));
  };

  const loadWorkloadData = async () => {
    try {
      const workloads = await workloadService.getProjectWorkloads(selectedProjectId);

      // Process workload data - get actual address counts from address service
      const workloadStats = await Promise.all(workloads.map(async (wl) => {
        // Get actual address stats from the addresses subcollection
        const addressStats = await addressService.getStats(wl.id);
        const totalAddresses = Object.values(addressStats).reduce((sum, count) => sum + count, 0);
        const completedAddresses = addressStats.completed || 0;
        const pendingAddresses = addressStats.pending || 0;
        const notHomeAddresses = addressStats.not_home || 0;

        return {
          id: wl.id,
          name: wl.name,
          status: wl.status,
          totalAddresses,
          completedAddresses,
          pendingAddresses,
          notHomeAddresses,
          completionRate: totalAddresses > 0
            ? Math.round((completedAddresses / totalAddresses) * 100)
            : 0,
          region: wl.territory?.name || wl.name,
          assignedTo: wl.assignedToNames || [],
        };
      }));

      // Group by region
      const regionMap = new Map<string, { count: number; total: number; completed: number }>();
      workloadStats.forEach(wl => {
        const region = wl.region || 'Unassigned';
        const existing = regionMap.get(region) || { count: 0, total: 0, completed: 0 };
        existing.count++;
        existing.total += wl.totalAddresses;
        existing.completed += wl.completedAddresses;
        regionMap.set(region, existing);
      });

      const regionSummary = Array.from(regionMap.entries()).map(([region, data]) => ({
        region,
        workloadCount: data.count,
        totalHomes: data.total,
        completedHomes: data.completed,
        completionRate: data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0,
      }));

      // Status distribution
      const statusCounts = new Map<string, number>();
      workloads.forEach(wl => {
        statusCounts.set(wl.status, (statusCounts.get(wl.status) || 0) + 1);
      });

      const statusDistribution = Array.from(statusCounts.entries()).map(([status, count]) => ({
        status,
        count,
        color: status === 'active' ? '#22C55E' : status === 'draft' ? '#9CA3AF' : status === 'completed' ? '#3B82F6' : '#F59E0B',
      }));

      setReportsData(prev => ({
        ...prev,
        workloadData: {
          workloads: workloadStats,
          regionSummary,
          statusDistribution,
        },
      }));
    } catch (error) {
      console.error('Error loading workload data:', error);
    }
  };

  const loadSurveyData = async () => {
    try {
      // Get survey responses for this project
      const responses = await surveyResponseService.getByProject(selectedProjectId);

      // Group by workload
      const workloadResponseMap = new Map<string, { name: string; responses: number; total: number }>();
      responses.forEach(r => {
        const workloadId = r.workloadId || 'unknown';
        const workloadName = r.workloadName || 'Unknown Workload';
        const existing = workloadResponseMap.get(workloadId) || { name: workloadName, responses: 0, total: 0 };
        existing.responses++;
        workloadResponseMap.set(workloadId, existing);
      });

      const responsesByWorkload = Array.from(workloadResponseMap.values()).map(data => ({
        workloadName: data.name,
        responses: data.responses,
        completionRate: 0, // Would need address data to calculate
      }));

      // Question breakdown (simplified - would need actual survey structure)
      const questionBreakdown: Array<{
        question: string;
        answers: Array<{ answer: string; count: number; percentage: number }>;
      }> = [];

      // Process responses if available (data stored as responses Record, not answers array)
      responses.forEach(r => {
        const responseData = r.responses || r.answers;
        if (responseData && typeof responseData === 'object') {
          Object.entries(responseData).forEach(([questionId, answer]) => {
            let existing = questionBreakdown.find(q => q.question === questionId);
            if (!existing) {
              existing = { question: questionId, answers: [] };
              questionBreakdown.push(existing);
            }
            const answerStr = Array.isArray(answer) ? answer.join(', ') : String(answer);
            let answerEntry = existing.answers.find(a => a.answer === answerStr);
            if (!answerEntry) {
              answerEntry = { answer: answerStr, count: 0, percentage: 0 };
              existing.answers.push(answerEntry);
            }
            answerEntry.count++;
          });
        }
      });

      // Calculate percentages
      questionBreakdown.forEach(q => {
        const total = q.answers.reduce((sum, a) => sum + a.count, 0);
        q.answers.forEach(a => {
          a.percentage = total > 0 ? Math.round((a.count / total) * 100) : 0;
        });
      });

      setReportsData(prev => ({
        ...prev,
        surveyData: {
          totalResponses: responses.length,
          responsesByWorkload,
          questionBreakdown: questionBreakdown.slice(0, 10), // Limit to first 10 questions
        },
      }));
    } catch (error) {
      console.error('Error loading survey data:', error);
    }
  };

  // ============================================
  // NEW COMPREHENSIVE REPORT LOADERS
  // ============================================

  const loadLifecycleReport = async (start: Date, end: Date) => {
    try {
      const report = await projectLifecycleService.generate(selectedProjectId, { start, end });
      setLifecycleReport(report);
    } catch (error) {
      console.error('Error loading lifecycle report:', error);
      toast.error('Failed to load project recap');
    }
  };

  const loadProjectOverview = async (start: Date, end: Date) => {
    try {
      const overview = await projectReportsService.generateProjectOverview(
        selectedProjectId,
        { start, end }
      );
      setProjectOverview(overview);

      // Load available surveys from both workloads and the surveys collection
      const workloads = await workloadService.getProjectWorkloads(selectedProjectId);
      const surveyIds = new Set<string>();
      const surveys: {id: string; name: string}[] = [];
      // Source 1: Surveys linked to workloads
      workloads.forEach(w => {
        if (w.surveyId && !surveyIds.has(w.surveyId)) {
          surveyIds.add(w.surveyId);
          surveys.push({ id: w.surveyId, name: w.surveyName || 'Survey' });
        }
      });
      // Source 2: Surveys created for this project (catches unlinked surveys)
      const projectSurveys = await surveyService.getProjectSurveys(selectedProjectId);
      projectSurveys.forEach(s => {
        if (!surveyIds.has(s.id)) {
          surveyIds.add(s.id);
          surveys.push({ id: s.id, name: s.name });
        }
      });
      setAvailableSurveys(surveys);
    } catch (error) {
      console.error('Error loading project overview:', error);
      toast.error('Failed to load project overview');
    }
  };

  const loadCanvasserPerformance = async (start: Date, end: Date) => {
    try {
      const summaries = await canvasserReportsService.getCanvasserSummaries(
        selectedProjectId,
        { start, end }
      );
      setCanvasserSummaries(summaries);
    } catch (error) {
      console.error('Error loading canvasser performance:', error);
      toast.error('Failed to load canvasser performance');
    }
  };

  const loadSurveyAnalytics = async (start: Date, end: Date) => {
    try {
      // Load available surveys from both workloads and the surveys collection
      const workloads = await workloadService.getProjectWorkloads(selectedProjectId);
      const surveyIds = new Set<string>();
      const surveys: {id: string; name: string}[] = [];
      // Source 1: Surveys linked to workloads
      workloads.forEach(w => {
        if (w.surveyId && !surveyIds.has(w.surveyId)) {
          surveyIds.add(w.surveyId);
          surveys.push({ id: w.surveyId, name: w.surveyName || 'Survey' });
        }
      });
      // Source 2: Surveys created for this project (catches unlinked surveys)
      const projectSurveys = await surveyService.getProjectSurveys(selectedProjectId);
      projectSurveys.forEach(s => {
        if (!surveyIds.has(s.id)) {
          surveyIds.add(s.id);
          surveys.push({ id: s.id, name: s.name });
        }
      });
      setAvailableSurveys(surveys);

      // Load analytics for selected survey (or first survey)
      const targetSurveyId = selectedSurveyId || surveys[0]?.id;
      if (targetSurveyId) {
        setSelectedSurveyId(targetSurveyId);
        const analytics = await surveyReportsService.generateSurveyAnalytics(
          targetSurveyId,
          { start, end },
          selectedProjectId // Filter by project
        );
        setSurveyAnalytics(analytics);
      }
    } catch (error) {
      console.error('Error loading survey analytics:', error);
      toast.error('Failed to load survey analytics');
    }
  };

  const handleSurveyChange = async (surveyId: string) => {
    setSelectedSurveyId(surveyId);
    const timeRangeOption = timeRangeOptions.find(t => t.value === timeRange);
    const { start, end } = timeRangeOption!.getDates();

    try {
      setLoading(true);
      const analytics = await surveyReportsService.generateSurveyAnalytics(
        surveyId,
        { start, end },
        selectedProjectId // Filter by project
      );
      setSurveyAnalytics(analytics);
    } catch (error) {
      console.error('Error loading survey analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: LeadStatus): string => {
    switch (status) {
      case 'uncontacted': return '#9CA3AF';
      case 'cold': return '#60A5FA';
      case 'warm': return '#FCD34D';
      case 'hot': return '#FB923C';
      case 'enrolled': return '#34D399';
      case 'lost': return '#F87171';
      case 'dnc': return '#6B7280';
      default: return '#9CA3AF';
    }
  };

  const getDeterminationColor = (det: LeadDetermination): string => {
    switch (det) {
      case 'pending': return '#FCD34D';
      case 'valid': return '#34D399';
      case 'invalid': return '#F87171';
      default: return '#FCD34D';
    }
  };

  const exportToPDF = async () => {
    try {
      toast.loading('Generating PDF...');

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const contentWidth = pdfWidth - (margin * 2);

      // Helper to add page header
      const addPageHeader = (title: string, pageNum: number, totalPages?: number) => {
        pdf.setFontSize(10);
        pdf.setTextColor(128, 128, 128);
        const pageText = totalPages ? `Page ${pageNum} of ${totalPages}` : `Page ${pageNum}`;
        pdf.text(pageText, pdfWidth - margin, 10, { align: 'right' });
        pdf.text(format(new Date(), 'MMM d, yyyy'), margin, 10);
      };

      // For Survey Analytics reports - use data-driven approach
      if (reportType === 'survey_analytics' && surveyAnalytics) {
        const projectName = selectedProject?.name || 'Project Report';
        const surveyName = surveyAnalytics.surveyName || 'Survey Report';
        const totalQuestions = surveyAnalytics.questionAnalytics.length;
        const hasCanvassers = surveyAnalytics.canvasserDistribution && surveyAnalytics.canvasserDistribution.length > 0;
        const totalPages = 1 + totalQuestions + (hasCanvassers ? 1 : 0);

        // =====================
        // PAGE 1: Title & Summary
        // =====================
        addPageHeader('Survey Analytics Report', 1, totalPages);

        // Report title
        pdf.setFontSize(24);
        pdf.setTextColor(17, 24, 39);
        pdf.text('Survey Analytics Report', margin, 35);

        // Project & Survey name
        pdf.setFontSize(14);
        pdf.setTextColor(75, 85, 99);
        pdf.text(projectName, margin, 47);
        pdf.setFontSize(12);
        pdf.setTextColor(107, 114, 128);
        pdf.text(`Survey: ${surveyName}`, margin, 55);

        // Divider line
        pdf.setDrawColor(229, 231, 235);
        pdf.setLineWidth(0.5);
        pdf.line(margin, 62, pdfWidth - margin, 62);

        // Summary metrics section
        pdf.setFontSize(16);
        pdf.setTextColor(17, 24, 39);
        pdf.text('Response Summary', margin, 75);

        // Metrics table
        const metricsData = [
          ['Total Responses', surveyAnalytics.responseMetrics.totalResponses.toString()],
          ['Completed', surveyAnalytics.responseMetrics.completedResponses.toString()],
          ['Discontinued', surveyAnalytics.responseMetrics.discontinuedResponses.toString()],
          ['Completion Rate', `${surveyAnalytics.responseMetrics.completionRate}%`],
          ['Avg. Completion Time', `${surveyAnalytics.responseMetrics.avgCompletionTime} min`],
        ];

        autoTable(pdf, {
          startY: 80,
          head: [['Metric', 'Value']],
          body: metricsData,
          theme: 'striped',
          headStyles: { fillColor: [99, 102, 241], textColor: 255 },
          styles: { fontSize: 11, cellPadding: 4 },
          columnStyles: {
            0: { fontStyle: 'bold', cellWidth: 80 },
            1: { halign: 'right', cellWidth: 50 }
          },
          margin: { left: margin, right: margin },
          tableWidth: 130,
        });

        // Response trend summary (text-based)
        const responsesByDay = surveyAnalytics.responseMetrics.responsesByDay || [];
        if (responsesByDay.length > 0) {
          const currentY = (pdf as any).lastAutoTable?.finalY || 130;

          pdf.setFontSize(14);
          pdf.setTextColor(17, 24, 39);
          pdf.text('Response Trend (Last 7 Days)', margin, currentY + 15);

          const trendData = responsesByDay.slice(-7).map(d => [d.date, d.count.toString()]);

          autoTable(pdf, {
            startY: currentY + 20,
            head: [['Date', 'Responses']],
            body: trendData,
            theme: 'grid',
            headStyles: { fillColor: [34, 197, 94], textColor: 255 },
            styles: { fontSize: 10, cellPadding: 3 },
            columnStyles: {
              0: { cellWidth: 60 },
              1: { halign: 'center', cellWidth: 40 }
            },
            margin: { left: margin, right: margin },
            tableWidth: 100,
          });
        }

        // =====================
        // QUESTION PAGES
        // =====================
        surveyAnalytics.questionAnalytics.forEach((q, index) => {
          pdf.addPage();
          addPageHeader('Survey Analytics Report', index + 2, totalPages);

          // Question header
          pdf.setFontSize(12);
          pdf.setTextColor(99, 102, 241);
          pdf.text(`Question ${index + 1} of ${totalQuestions}`, margin, 25);

          // Question text - wrap long text
          pdf.setFontSize(14);
          pdf.setTextColor(17, 24, 39);
          const questionLines = pdf.splitTextToSize(q.questionText, contentWidth);
          pdf.text(questionLines, margin, 35);

          const textHeight = questionLines.length * 6;
          let currentY = 35 + textHeight + 5;

          // Question type badge
          pdf.setFontSize(10);
          pdf.setTextColor(107, 114, 128);
          pdf.text(`Type: ${q.questionType}${q.required ? ' (Required)' : ''}`, margin, currentY);
          currentY += 10;

          // Response stats
          pdf.setFontSize(11);
          pdf.setTextColor(55, 65, 81);
          pdf.text(`Total Responses: ${q.totalResponses}  |  Skipped: ${q.skippedCount}  |  Response Rate: ${q.responseRate}%`, margin, currentY);
          currentY += 15;

          // Choice distribution table (for applicable question types)
          if (q.choiceDistribution && q.choiceDistribution.length > 0) {
            pdf.setFontSize(12);
            pdf.setTextColor(17, 24, 39);
            pdf.text('Response Distribution', margin, currentY);
            currentY += 5;

            const choiceData = q.choiceDistribution.map(choice => [
              choice.value,
              choice.count.toString(),
              `${choice.percentage.toFixed(1)}%`
            ]);

            autoTable(pdf, {
              startY: currentY,
              head: [['Response', 'Count', 'Percentage']],
              body: choiceData,
              theme: 'striped',
              headStyles: { fillColor: [79, 70, 229], textColor: 255 },
              styles: { fontSize: 10, cellPadding: 4 },
              columnStyles: {
                0: { cellWidth: 100 },
                1: { halign: 'center', cellWidth: 30 },
                2: { halign: 'right', cellWidth: 35 }
              },
              margin: { left: margin, right: margin },
            });

            // Add visual bar representation
            const tableEndY = (pdf as any).lastAutoTable?.finalY || currentY + 50;
            let barY = tableEndY + 10;

            if (barY < pdfHeight - 60) {
              pdf.setFontSize(11);
              pdf.setTextColor(75, 85, 99);
              pdf.text('Visual Distribution:', margin, barY);
              barY += 8;

              const maxBarWidth = contentWidth - 50;
              const maxPercentage = Math.max(...q.choiceDistribution.map(c => c.percentage));

              q.choiceDistribution.slice(0, 8).forEach((choice, i) => {
                if (barY > pdfHeight - 20) return;

                const barWidth = maxPercentage > 0 ? (choice.percentage / maxPercentage) * maxBarWidth : 0;

                // Bar background
                pdf.setFillColor(229, 231, 235);
                pdf.rect(margin, barY, maxBarWidth, 6, 'F');

                // Bar fill (gradient colors based on index)
                const colors = [
                  [99, 102, 241], [34, 197, 94], [249, 115, 22], [236, 72, 153],
                  [14, 165, 233], [168, 85, 247], [234, 179, 8], [239, 68, 68]
                ];
                const color = colors[i % colors.length];
                pdf.setFillColor(color[0], color[1], color[2]);
                pdf.rect(margin, barY, barWidth, 6, 'F');

                // Label
                pdf.setFontSize(8);
                pdf.setTextColor(55, 65, 81);
                const truncatedLabel = choice.value.length > 25 ? choice.value.substring(0, 22) + '...' : choice.value;
                pdf.text(`${truncatedLabel} (${choice.percentage.toFixed(1)}%)`, margin + maxBarWidth + 3, barY + 4.5);

                barY += 10;
              });
            }
          }

          // Numeric stats (for numeric questions)
          if (q.numericStats) {
            pdf.setFontSize(12);
            pdf.setTextColor(17, 24, 39);
            pdf.text('Numeric Statistics', margin, currentY);

            const numericData = [
              ['Minimum', q.numericStats.min.toString()],
              ['Maximum', q.numericStats.max.toString()],
              ['Average', q.numericStats.avg.toFixed(2)],
              ['Median', q.numericStats.median.toString()],
              ['Mode', q.numericStats.mode.toString()],
            ];

            autoTable(pdf, {
              startY: currentY + 5,
              head: [['Statistic', 'Value']],
              body: numericData,
              theme: 'grid',
              headStyles: { fillColor: [34, 197, 94], textColor: 255 },
              styles: { fontSize: 10, cellPadding: 3 },
              margin: { left: margin, right: margin },
              tableWidth: 100,
            });
          }

          // Text stats (for text questions)
          if (q.textStats) {
            pdf.setFontSize(12);
            pdf.setTextColor(17, 24, 39);
            pdf.text('All Text Responses', margin, currentY);
            currentY += 5;

            // Show all text responses in a table
            if (q.textStats.allResponses && q.textStats.allResponses.length > 0) {
              const responseData = q.textStats.allResponses.map((response, i) => [
                (i + 1).toString(),
                response
              ]);

              autoTable(pdf, {
                startY: currentY,
                head: [['#', 'Response']],
                body: responseData,
                theme: 'striped',
                headStyles: { fillColor: [249, 115, 22], textColor: 255 },
                styles: { fontSize: 9, cellPadding: 3 },
                columnStyles: {
                  0: { cellWidth: 12, halign: 'center' },
                  1: { cellWidth: contentWidth - 20 }
                },
                margin: { left: margin, right: margin },
                // Handle page breaks automatically for long response lists
                showHead: 'everyPage',
                didDrawPage: (data) => {
                  // Add header on continuation pages
                  if (data.pageNumber > 1) {
                    pdf.setFontSize(10);
                    pdf.setTextColor(128, 128, 128);
                    pdf.text(`Question ${index + 1} (continued)`, margin, 15);
                  }
                }
              });
            } else {
              pdf.setFontSize(10);
              pdf.setTextColor(107, 114, 128);
              pdf.text('No text responses recorded', margin, currentY + 5);
            }
          }
        });

        // =====================
        // CANVASSER SUMMARY PAGE
        // =====================
        if (hasCanvassers) {
          pdf.addPage();
          addPageHeader('Survey Analytics Report', totalPages, totalPages);

          pdf.setFontSize(16);
          pdf.setTextColor(17, 24, 39);
          pdf.text('Canvasser Performance Summary', margin, 30);

          pdf.setFontSize(11);
          pdf.setTextColor(107, 114, 128);
          pdf.text(`${surveyAnalytics.canvasserDistribution.length} canvassers collected responses`, margin, 40);

          const canvasserData = surveyAnalytics.canvasserDistribution
            .sort((a, b) => b.responseCount - a.responseCount)
            .map((c, i) => [
              (i + 1).toString(),
              c.userName,
              c.responseCount.toString(),
              `${c.avgCompletionTime} min`
            ]);

          autoTable(pdf, {
            startY: 48,
            head: [['#', 'Canvasser', 'Responses', 'Avg. Time']],
            body: canvasserData,
            theme: 'striped',
            headStyles: { fillColor: [79, 70, 229], textColor: 255 },
            styles: { fontSize: 10, cellPadding: 4 },
            columnStyles: {
              0: { cellWidth: 15, halign: 'center' },
              1: { cellWidth: 80 },
              2: { cellWidth: 35, halign: 'center' },
              3: { cellWidth: 35, halign: 'center' }
            },
            margin: { left: margin, right: margin },
          });
        }

      } else if (reportType === 'lifecycle' && lifecycleReport) {
        await buildLifecyclePdf(pdf, lifecycleReport, process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '');
      } else {
        // For non-survey reports, create a simple text-based summary
        addPageHeader('Report', 1);

        pdf.setFontSize(20);
        pdf.setTextColor(17, 24, 39);
        pdf.text(`${reportType.charAt(0).toUpperCase() + reportType.slice(1).replace('_', ' ')} Report`, margin, 35);

        pdf.setFontSize(12);
        pdf.setTextColor(107, 114, 128);
        pdf.text(selectedProject?.name || 'All Projects', margin, 45);
        pdf.text(`Generated: ${format(new Date(), 'MMMM d, yyyy')}`, margin, 53);

        // Add basic data tables for other report types
        if (reportType === 'performance' && reportsData.fieldStaffPerformance.length > 0) {
          pdf.setFontSize(14);
          pdf.setTextColor(17, 24, 39);
          pdf.text('Field Staff Performance', margin, 70);

          const perfData = reportsData.fieldStaffPerformance.map(s => [
            s.name,
            s.leads.toString(),
            s.conversions.toString(),
            `${s.conversionRate.toFixed(1)}%`
          ]);

          autoTable(pdf, {
            startY: 75,
            head: [['Staff', 'Leads', 'Conversions', 'Rate']],
            body: perfData,
            theme: 'striped',
            headStyles: { fillColor: [99, 102, 241], textColor: 255 },
            margin: { left: margin, right: margin },
          });
        }

        if (reportType === 'statusbreakdown' && reportsData.statusBreakdown.length > 0) {
          pdf.setFontSize(14);
          pdf.setTextColor(17, 24, 39);
          pdf.text('Lead Status Breakdown', margin, 70);

          const statusData = reportsData.statusBreakdown.map(s => [
            s.status.charAt(0).toUpperCase() + s.status.slice(1),
            s.count.toString(),
            `${s.percentage.toFixed(1)}%`
          ]);

          autoTable(pdf, {
            startY: 75,
            head: [['Status', 'Count', 'Percentage']],
            body: statusData,
            theme: 'striped',
            headStyles: { fillColor: [99, 102, 241], textColor: 255 },
            margin: { left: margin, right: margin },
          });
        }
      }

      const projectForFileName = projects.find(p => p.id === selectedProjectId);
      const fileName = `${projectForFileName?.name || 'report'}_${reportType}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;

      toast.dismiss();
      pdf.save(fileName);
      toast.success('Report exported to PDF');
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.dismiss();
      toast.error('Failed to export PDF');
    }
  };

  const renderReportContent = () => {
    switch (reportType) {
      case 'lifecycle':
        return <ProjectLifecycleReport report={lifecycleReport} loading={loading} />;
      case 'performance':
        return (
          <PerformanceReport
            dailyGeneration={reportsData.dailyGeneration}
            fieldStaffPerformance={reportsData.fieldStaffPerformance}
          />
        );
      case 'source':
        return <SourceAnalysisReport sourceAnalysis={reportsData.sourceAnalysis} />;
      case 'demographics':
        return <DemographicsReport demographicsData={reportsData.demographicsData} />;
      case 'statusbreakdown':
        return <StatusBreakdownReport statusBreakdown={reportsData.statusBreakdown} />;
      case 'determinationbreakdown':
        return <DeterminationBreakdownReport determinationBreakdown={reportsData.determinationBreakdown} />;
      case 'timerange':
        return (
          <TimeRangeReport
            timeRangeData={reportsData.timeRangeData}
            dailyGeneration={reportsData.dailyGeneration}
          />
        );
      case 'workloads':
        return <WorkloadsReport workloadData={reportsData.workloadData} />;
      case 'surveys':
        return <SurveysReport surveyData={reportsData.surveyData} />;
      case 'project_overview':
        return <ProjectOverviewReport projectOverview={projectOverview} loading={loading} />;
      case 'canvasser_performance':
        return <CanvasserPerformanceReport canvasserSummaries={canvasserSummaries} loading={loading} />;
      case 'survey_analytics':
        return (
          <SurveyAnalyticsReport
            surveyAnalytics={surveyAnalytics}
            availableSurveys={availableSurveys}
            selectedSurveyId={selectedSurveyId}
            onSurveyChange={handleSurveyChange}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#a4de6c', '#d0ed57', '#ffc658'];

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
            <p className="mt-1 text-sm text-gray-600">
              {selectedProject ? `Viewing: ${selectedProject.name}` : 'Select a project from the navigation'}
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={exportToPDF}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
              disabled={loading || !selectedProjectId}
            >
              <Download className="h-4 w-4 mr-2" />
              PDF
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Type
              </label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value as ReportType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={!selectedProjectId}
              >
                <optgroup label="Project Recap">
                  <option value="lifecycle">Project Lifecycle Recap</option>
                </optgroup>
                <optgroup label="Lead Generation Reports">
                  <option value="performance">Performance Analysis</option>
                  <option value="source">Source Analysis</option>
                  <option value="demographics">Demographics</option>
                  <option value="timerange">Time Range Analysis</option>
                  <option value="statusbreakdown">Status Breakdown</option>
                  <option value="determinationbreakdown">Determination Breakdown</option>
                </optgroup>
                <optgroup label="Canvassing Reports">
                  <option value="workloads">Workload Reports</option>
                  <option value="project_overview">Project Overview</option>
                  <option value="canvasser_performance">Canvasser Performance</option>
                  <option value="survey_analytics">Survey Analytics</option>
                </optgroup>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Time Range
              </label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                disabled={!selectedProjectId}
              >
                {timeRangeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={loadReportData}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                disabled={loading || selectedProjectId === 'all'}
              >
                {loading ? 'Loading...' : 'Refresh Data'}
              </button>
            </div>
          </div>
        </div>

        {selectedProjectId && (
          <div ref={reportRef}>
            {renderReportContent()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;